import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppContext, AppContextValue } from "../contexts/app-context";
import { ExamConfiguration } from "../hooks/use-exam-config-mapping";
import { useInterval } from "../hooks/use-interval";
import { getFreezeState } from "../lib/adminHelper";
import appInsights from "../lib/app-insights";

const env = process.env.REACT_APP_ENV as string;
const DEFAULT_FREEZE_POLL_INTERVAL_SECONDS = 30;
const freezePollIntervalSeconds = Number(process.env.REACT_APP_FREEZE_POLL_INTERVAL_SECONDS);
const FREEZE_POLL_INTERVAL_MS =
  (Number.isFinite(freezePollIntervalSeconds) && freezePollIntervalSeconds > 0
    ? Math.max(freezePollIntervalSeconds, DEFAULT_FREEZE_POLL_INTERVAL_SECONDS)
    : DEFAULT_FREEZE_POLL_INTERVAL_SECONDS) * 1000;

interface AppContextProviderProps {
  children?: ReactNode;
}

function AppContextProvider({ children }: AppContextProviderProps) {
  const [examConfig, setExamConfig] = useState<ExamConfiguration>();
  const [candidateInfo, setCandidateInfo] = useState<any>();
  const [isFreezeActive, setIsFreezeActive] = useState(false);
  const candidateInfoRef = useRef<any>(undefined);
  const wasFreezeActiveRef = useRef(false);

  useEffect(() => {
    candidateInfoRef.current = candidateInfo;
  }, [candidateInfo]);

  const refreshFreezeState = useCallback(async () => {
    try {
      const state = await getFreezeState();
      const active = Boolean(state?.isFreezeActive);
      const confirmationNumber = candidateInfoRef.current?.confirmationNumber;
      if (active && !wasFreezeActiveRef.current && confirmationNumber) {
        const payload = {
          CandidateIdentifier: confirmationNumber,
          Environment: env,
          ApplicationSurface: 'Candidate Portal',
          Reason: 'freeze activated',
          UTCTimestamp: new Date().toISOString(),
        };
        const message = `Freeze state blocked candidate from Exam Detail page ${JSON.stringify(payload)}`;
        appInsights.logWarning(message);
      }
      wasFreezeActiveRef.current = active;
      setIsFreezeActive(active);
      return active;
    } catch {
      setIsFreezeActive(false);
      return false;
    }
  }, []);

  const { start: startFreezePolling, stop: stopFreezePolling } = useInterval(
    () => {
      void refreshFreezeState();
    },
    FREEZE_POLL_INTERVAL_MS,
  );

  useEffect(() => {
    if (process.env.REACT_APP_FREEZE_CHECK_ENABLED !== "true") {
      setIsFreezeActive(false);
      stopFreezePolling();
      return;
    }

    void refreshFreezeState();
    startFreezePolling();

    return () => {
      stopFreezePolling();
    };
  }, [refreshFreezeState, startFreezePolling, stopFreezePolling]);

  const setExamConfigState = (config: ExamConfiguration) => {
    setExamConfig(config);
  };

  const setCandidateInfoState = (candidateInfo: any) => {
    setCandidateInfo(candidateInfo);
  };

  const clearState = () => {
    setExamConfig(undefined);
    setCandidateInfo(undefined);
    setIsFreezeActive(Boolean(wasFreezeActiveRef.current));
  };

  const contextValue = useMemo<AppContextValue>(
    () => ({
      examConfig: examConfig,
      candidateInfo: candidateInfo,
      isFreezeActive: isFreezeActive,
      setExamConfig: setExamConfigState,
      setCandidateInfo: setCandidateInfoState,
      setIsFreezeActive: setIsFreezeActive,
      refreshFreezeState: refreshFreezeState,
      startFreezePolling: startFreezePolling,
      stopFreezePolling: stopFreezePolling,
      clearState: clearState,
    }),
    [examConfig, candidateInfo, isFreezeActive, refreshFreezeState, startFreezePolling, stopFreezePolling],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

export default AppContextProvider;
